import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaUserPlus, FaPaperPlane, FaTrash, FaEdit, FaUserShield, FaUser, FaEye, FaClock, FaEnvelope, FaCheck, FaTimes } from 'react-icons/fa';
import { useAuth0 } from '@auth0/auth0-react';
import { useToast } from '../../Context/ToastContext';

const Container = styled.div`
  background: #1a1f2e;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h3`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Badge = styled.span`
  background: #8b5cf6;
  color: white;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
  }
`;

const MembersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MemberCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #252b3d;
  border-radius: 10px;
  border: 1px solid #3a4055;
  transition: all 0.2s ease;

  &:hover {
    border-color: #8b5cf6;
  }
`;

const MemberInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.$pending ? '#4b5563' : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  font-weight: 600;
`;

const MemberDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MemberName = styled.span`
  color: #fff;
  font-size: 16px;
  font-weight: 500;
`;

const MemberEmail = styled.span`
  color: #94a3b8;
  font-size: 13px;
`;

const MemberMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  
  ${props => {
    switch(props.$role) {
      case 'admin':
        return `background: rgba(239, 68, 68, 0.15); color: #f87171;`;
      case 'member':
        return `background: rgba(59, 130, 246, 0.15); color: #60a5fa;`;
      case 'viewer':
        return `background: rgba(34, 197, 94, 0.15); color: #4ade80;`;
      default:
        return `background: rgba(148, 163, 184, 0.15); color: #94a3b8;`;
    }
  }}
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  
  ${props => {
    switch(props.$status) {
      case 'active':
        return `background: rgba(34, 197, 94, 0.15); color: #4ade80;`;
      case 'pending':
        return `background: rgba(251, 191, 36, 0.15); color: #fbbf24;`;
      case 'suspended':
        return `background: rgba(239, 68, 68, 0.15); color: #f87171;`;
      default:
        return `background: rgba(148, 163, 184, 0.15); color: #94a3b8;`;
    }
  }}
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$variant === 'danger' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(139, 92, 246, 0.1)'};
  color: ${props => props.$variant === 'danger' ? '#f87171' : '#a78bfa'};

  &:hover {
    background: ${props => props.$variant === 'danger' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(139, 92, 246, 0.2)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #94a3b8;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  box-sizing: border-box;
`;

const Modal = styled.div`
  background: #1a1f2e;
  border-radius: 16px;
  padding: 32px;
  width: 100%;
  max-width: 500px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  border: 1px solid rgba(139, 92, 246, 0.3);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: modalSlideIn 0.3s ease-out;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  color: #fff;
  font-size: 20px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 20px;
  cursor: pointer;
  
  &:hover {
    color: #fff;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: #94a3b8;
  font-size: 13px;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: #252b3d;
  border: 1px solid #3a4055;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
  }

  &::placeholder {
    color: #64748b;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  background: #252b3d;
  border: 1px solid #3a4055;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
  }

  option {
    background: #252b3d;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.$primary ? `
    background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
    color: white;
    border: none;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
    }
  ` : `
    background: transparent;
    color: #94a3b8;
    border: 1px solid #3a4055;

    &:hover {
      background: #252b3d;
      color: #fff;
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LastActive = styled.span`
  color: #64748b;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

// API URL
const API_BASE = process.env.REACT_APP_SERVER || 'http://localhost:3030/';

export default function TeamManagement() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'admin', // Default to admin (first member must be admin)
    title: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Check if this is the first member (must be admin)
  const isFirstMember = members.length === 0;
  
  const { getAccessTokenSilently } = useAuth0();
  const { showToast } = useToast();

  const fetchMembers = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}team/members`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMembers(data.members || []);
      }
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleAddMember = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Force admin role for first member
    const dataToSubmit = {
      ...formData,
      role: isFirstMember ? 'admin' : formData.role
    };
    
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}team/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSubmit)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showToast('Invitation sent successfully!', 'success');
        setShowAddModal(false);
        setFormData({ name: '', email: '', role: 'member', title: '' });
        fetchMembers();
      } else {
        showToast(data.error || 'Failed to add team member', 'error');
      }
    } catch (error) {
      showToast('Failed to add team member', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    if (!editingMember) return;
    setSubmitting(true);
    
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}team/members/${editingMember._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          role: formData.role,
          title: formData.title
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showToast('Team member updated', 'success');
        setShowEditModal(false);
        setEditingMember(null);
        fetchMembers();
      } else {
        showToast(data.error || 'Failed to update team member', 'error');
      }
    } catch (error) {
      showToast('Failed to update team member', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvite = async (memberId) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}team/members/${memberId}/resend-invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        showToast('Invitation resent', 'success');
      } else {
        const data = await response.json();
        showToast(data.error || 'Failed to resend invitation', 'error');
      }
    } catch (error) {
      showToast('Failed to resend invitation', 'error');
    }
  };

  const handleDeleteMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from the team?`)) {
      return;
    }
    
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}team/members/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        showToast('Team member removed', 'success');
        fetchMembers();
      } else {
        const data = await response.json();
        showToast(data.error || 'Failed to remove team member', 'error');
      }
    } catch (error) {
      showToast('Failed to remove team member', 'error');
    }
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      email: member.email,
      role: member.role,
      title: member.title || ''
    });
    setShowEditModal(true);
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatLastActive = (date) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'admin': return <FaUserShield size={10} />;
      case 'member': return <FaUser size={10} />;
      case 'viewer': return <FaEye size={10} />;
      default: return <FaUser size={10} />;
    }
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>
            <FaUser /> Team Members
          </Title>
        </Header>
        <EmptyState>Loading team members...</EmptyState>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <Header>
          <Title>
            <FaUser /> Team Members
            <Badge>{members.length}</Badge>
          </Title>
          <AddButton onClick={() => setShowAddModal(true)}>
            <FaUserPlus /> Add Member
          </AddButton>
        </Header>

        {members.length === 0 ? (
          <EmptyState>
            <EmptyIcon>👥</EmptyIcon>
            <h4 style={{ color: '#fff', marginBottom: 8 }}>No team members yet</h4>
            <p>Invite your team to collaborate on cases and conversations.</p>
          </EmptyState>
        ) : (
          <MembersList>
            {members.map(member => (
              <MemberCard key={member._id}>
                <MemberInfo>
                  <Avatar $pending={member.status === 'pending'}>
                    {getInitials(member.name)}
                  </Avatar>
                  <MemberDetails>
                    <MemberName>{member.name}</MemberName>
                    <MemberEmail>{member.email}</MemberEmail>
                    <MemberMeta>
                      <RoleBadge $role={member.role}>
                        {getRoleIcon(member.role)} {member.role}
                      </RoleBadge>
                      <StatusBadge $status={member.status}>
                        {member.status === 'active' && <FaCheck size={8} />}
                        {member.status === 'pending' && <FaClock size={8} />}
                        {member.status}
                      </StatusBadge>
                      {member.status === 'active' && member.lastActivityAt && (
                        <LastActive>
                          <FaClock size={10} /> {formatLastActive(member.lastActivityAt)}
                        </LastActive>
                      )}
                    </MemberMeta>
                  </MemberDetails>
                </MemberInfo>
                <Actions>
                  {member.status === 'pending' && (
                    <ActionButton 
                      onClick={() => handleResendInvite(member._id)}
                      title="Resend invitation"
                    >
                      <FaEnvelope />
                    </ActionButton>
                  )}
                  <ActionButton onClick={() => openEditModal(member)} title="Edit member">
                    <FaEdit />
                  </ActionButton>
                  <ActionButton 
                    $variant="danger" 
                    onClick={() => handleDeleteMember(member._id, member.name)}
                    title="Remove member"
                  >
                    <FaTrash />
                  </ActionButton>
                </Actions>
              </MemberCard>
            ))}
          </MembersList>
        )}
      </Container>

      {/* Add Member Modal */}
      {showAddModal && (
        <ModalOverlay onClick={() => setShowAddModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Add Team Member</ModalTitle>
              <CloseButton onClick={() => setShowAddModal(false)}>×</CloseButton>
            </ModalHeader>
            
            <form onSubmit={handleAddMember}>
              <FormGroup>
                <Label>Full Name *</Label>
                <Input
                  type="text"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  placeholder="john@company.com"
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Role * {isFirstMember && <span style={{ color: '#fbbf24', fontSize: '11px' }}>(First member must be Admin)</span>}</Label>
                <Select 
                  value={isFirstMember ? 'admin' : formData.role}
                  onChange={e => setFormData({...formData, role: e.target.value})}
                  disabled={isFirstMember}
                  style={isFirstMember ? { opacity: 0.7 } : {}}
                >
                  {isFirstMember ? (
                    <option value="admin">Admin - Full access including settings (Required)</option>
                  ) : (
                    <>
                      <option value="member">Member - Can view and edit cases</option>
                      <option value="admin">Admin - Full access including settings</option>
                      <option value="viewer">Viewer - View only access</option>
                    </>
                  )}
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Job Title (optional)</Label>
                <Input
                  type="text"
                  placeholder="e.g., Paralegal, Case Manager"
                  value={formData.title}
                  onChange={e => setFormData({...formData, title: e.target.value})}
                />
              </FormGroup>
              
              <ButtonGroup>
                <Button type="button" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" $primary disabled={submitting}>
                  <FaPaperPlane style={{ marginRight: 8 }} />
                  {submitting ? 'Sending...' : 'Send Invitation'}
                </Button>
              </ButtonGroup>
            </form>
          </Modal>
        </ModalOverlay>
      )}

      {/* Edit Member Modal */}
      {showEditModal && editingMember && (
        <ModalOverlay onClick={() => setShowEditModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Edit Team Member</ModalTitle>
              <CloseButton onClick={() => setShowEditModal(false)}>×</CloseButton>
            </ModalHeader>
            
            <form onSubmit={handleUpdateMember}>
              <FormGroup>
                <Label>Full Name *</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={formData.email}
                  disabled
                  style={{ opacity: 0.6 }}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Role *</Label>
                <Select 
                  value={formData.role}
                  onChange={e => setFormData({...formData, role: e.target.value})}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Job Title</Label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({...formData, title: e.target.value})}
                />
              </FormGroup>
              
              <ButtonGroup>
                <Button type="button" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" $primary disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </ButtonGroup>
            </form>
          </Modal>
        </ModalOverlay>
      )}
    </>
  );
}
